import {
  Box,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductInterface } from "../../constants/Product";
import useCustomToast from "../../lib/useCustomToast";
import useScreenWidth from "../../lib/useScreenWidth";
import { useSearchStore } from "../../store/useSearchStore";
import { useDarkWhiteColor } from "../../themes/colors";
import { axiosPublic } from "../../utils/AxiosInstance";
import { Empty } from "../card/Empty";
import SearchInput from "../input/SearchInput";

const CardProductRecomend = lazy(() => import("../card/CardProductRecomend"));

interface Props extends PopoverProps {}

export const PopoverHeaderSearch = ({ ...rest }: Props) => {
  const [data, setData] = useState<ProductInterface[] | undefined>(undefined);
  const { query, setQuery } = useSearchStore();
  const toast = useCustomToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgBnw = useDarkWhiteColor();
  const sw = useScreenWidth();
  const navigate = useNavigate();

  // Create a ref to track the SearchInput width
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [inputWidth, setInputWidth] = useState(0);

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    // Measure the width of the SearchInput once it's rendered
    if (searchInputRef.current) {
      setInputWidth(searchInputRef.current.offsetWidth);
    }
  }, [searchInputRef.current]); // Re-run when the ref is updated

  useEffect(() => {
    if (isOpen) {
      axiosPublic
        .post(`/v1/landing/reseller/search-recomendation`, { search: query })
        .then((res) => {
          setData(res.data.data);
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {});
    }
  }, [query, isOpen]);

  useEffect(() => {
    if (isFocused === false) {
      onClose();
    }
  }, [isFocused, onClose]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsFocused(true);
    onOpen(); // Open the popover
    // Use setTimeout to delay focus until after the popover state has been updated
    setTimeout(() => {
      searchInputRef.current?.focus(); // Focus the input after popover is opened
    }, 0); // Delay to ensure the state update is processed
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onClose();
      navigate(`/product?search=${searchInputRef.current?.value}`);
      setQuery("");
    }
  };

  return (
    <Popover isOpen={isOpen} placement="bottom" {...rest}>
      <PopoverTrigger>
        <Box flex={1}>
          <SearchInput
            w={"full"}
            paddingY={4}
            paddingLeft={10}
            paddingRight={4}
            groupPaddingX={2}
            fontSize="sm"
            boxSize="15px"
            inputRef={searchInputRef}
            placeholder="Cari produk..."
            _placeholder={{ fontSize: "sm" }}
            onConfirm={(input) => setQuery(input)}
            onFocus={(e) => handleFocus(e)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        </Box>
      </PopoverTrigger>

      <PopoverContent w={inputWidth ? `${inputWidth}px` : "100%"} bg={bgBnw}>
        <PopoverBody>
          <VStack w={"100%"} align={"stretch"}>
            {query && (
              <HStack gap={1}>
                <Text fontSize={[10, null, 12]}>
                  Menampilkan pencarian untuk filter
                </Text>
                <Text fontSize={[10, null, 12]} fontWeight={"semibold"}>
                  {`"${query}"`}
                </Text>
              </HStack>
            )}

            {data && data.length > 0 ? (
              data.map((item) => (
                <Suspense fallback={<Skeleton height="50px" />} key={item.id}>
                  <CardProductRecomend
                    key={item.id}
                    src={item.main_picture}
                    name={item.name}
                    slug={item.slug}
                    isSmall={sw < 480}
                  />
                </Suspense>
              ))
            ) : (
              <Empty p={4} mx={"auto"} />
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
